import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { Match, Bracket, Prediction, PredictionInput } from '@/types/bracket'
import type { Round } from '@/types/bracket'
import type { RoundState } from '@/api/rounds'
import { MatchCard } from './MatchCard'
import { Spinner } from '@/components/ui/Spinner'

interface LocalScore {
  home: number | null
  away: number | null
}

interface BracketTreeProps {
  bracket: Bracket
  predictions: Prediction[]
  isLocked: boolean
  activeRound: Round | null
  rounds: RoundState[]
  onSubmit: (inputs: PredictionInput[]) => Promise<void>
  isSubmitting: boolean
}

const ROUND_ORDER: Round[] = ['R32', 'R16', 'QF', 'SF', 'FINAL']
const MATCH_LOCK_BUFFER_SECS = 30 * 60

export function BracketTree({ bracket, predictions, isLocked, activeRound, rounds, onSubmit, isSubmitting }: BracketTreeProps) {
  const { t } = useTranslation()
  const [localWinners, setLocalWinners] = useState<Map<string, string>>(new Map())
  const [localScores, setLocalScores] = useState<Map<string, LocalScore>>(new Map())
  const [showScores, setShowScores] = useState(false)
  const [nowSecs, setNowSecs] = useState(() => Math.floor(Date.now() / 1000))

  useEffect(() => {
    const id = setInterval(() => setNowSecs(Math.floor(Date.now() / 1000)), 10_000)
    return () => clearInterval(id)
  }, [])

  const isMatchIndividuallyLocked = useCallback((m: Match): boolean => {
    if (m.status === 'LIVE' || m.status === 'FINISHED') return true
    if (!m.scheduled_at) return false
    return nowSecs >= m.scheduled_at - MATCH_LOCK_BUFFER_SECS
  }, [nowSecs])

  const predMap = useMemo(
    () => new Map(predictions.map(p => [p.match_id, p])),
    [predictions]
  )

  // Effective teams come ONLY from actual DB data (real results propagated by the engine).
  // We do NOT cascade local picks into future rounds — round-by-round mode means
  // you only predict the current open round with real teams.
  const effectiveTeams = useMemo(() => {
    const map = new Map<string, { home?: string; away?: string }>()
    const allMatches: Match[] = bracket.rounds.flatMap(r => r.matches)
    for (const m of allMatches) {
      map.set(m.id, {
        home: m.home_team_id ?? undefined,
        away: m.away_team_id ?? undefined,
      })
    }
    return map
  }, [bracket])

  const handlePickTeam = useCallback((matchId: string, teamId: string) => {
    setLocalWinners(prev => new Map(prev).set(matchId, teamId))
  }, [])

  const handleScoreChange = useCallback((matchId: string, home: number | null, away: number | null) => {
    setLocalScores(prev => new Map(prev).set(matchId, { home, away }))
  }, [])

  const roundStatusMap = useMemo(
    () => new Map(rounds.map(r => [r.round, r.status])),
    [rounds]
  )

  // All matches across every non-SCORED round that have confirmed teams and aren't individually locked.
  // Once a matchup is decided (both teams known), users can predict it immediately.
  const pickableMatches = useMemo(() =>
    bracket.rounds
      .filter(r => roundStatusMap.get(r.round) !== 'SCORED')
      .flatMap(r => r.matches)
      .filter(m => {
        const eff = effectiveTeams.get(m.id)
        return eff?.home && eff?.away && !isMatchIndividuallyLocked(m)
      }),
    [bracket, roundStatusMap, effectiveTeams, isMatchIndividuallyLocked]
  )

  const pickedCount = pickableMatches.filter(m =>
    localWinners.get(m.id) ?? predMap.get(m.id)?.predicted_winner_id
  ).length

  const totalPickable = pickableMatches.length

  const handleSubmit = async () => {
    const inputs: PredictionInput[] = pickableMatches
      .map(m => {
        const winnerId = localWinners.get(m.id) ?? predMap.get(m.id)?.predicted_winner_id
        if (!winnerId) return null
        const score = localScores.get(m.id)
        return {
          matchId: m.id,
          winnerId,
          homeScore: score?.home ?? predMap.get(m.id)?.predicted_home_score ?? null,
          awayScore: score?.away ?? predMap.get(m.id)?.predicted_away_score ?? null,
        } satisfies PredictionInput
      })
      .filter(Boolean) as PredictionInput[]
    await onSubmit(inputs)
  }

  const renderRound = (bracketRounds: typeof bracket.rounds, roundIds: Round[]) => (
    <div className="flex gap-4 items-start">
      {bracketRounds
        .filter(r => roundIds.includes(r.round))
        .map(({ round, matches }) => {
          const roundStatus = roundStatusMap.get(round)
          // A round is editable when it isn't SCORED and there's no global emergency lock.
          // Being PENDING (not yet officially OPEN) is fine — any match with confirmed teams
          // can be predicted as soon as it becomes available.
          const isEditable = !isLocked && roundStatus !== 'SCORED'
          const isActiveRound = round === activeRound
          return (
            <div key={round} className="flex flex-col gap-2 min-w-[220px]">
              <div className="text-center mb-2">
                <span className="text-xs font-bold uppercase tracking-widest text-white/50">
                  {t(`bracket.rounds.${round}`)}
                </span>
                {isEditable && isActiveRound && (
                  <span className="ml-2 text-[10px] font-bold text-emerald-400 uppercase tracking-wide">
                    {t('bracket.pickNow')}
                  </span>
                )}
                {isEditable && !isActiveRound && roundStatus === 'PENDING' && matches.some(m => {
                  const eff = effectiveTeams.get(m.id)
                  return eff?.home && eff?.away
                }) && (
                  <span className="ml-2 text-[10px] font-bold text-amber-400 uppercase tracking-wide">
                    {t('bracket.matchupsReady')}
                  </span>
                )}
              </div>
              <div
                className="flex flex-col gap-3"
                style={{ gap: round === 'R32' ? '8px' : round === 'R16' ? '80px' : round === 'QF' ? '168px' : round === 'SF' ? '344px' : '8px' }}
              >
                {matches.map(m => {
                  const eff = effectiveTeams.get(m.id)
                  const homeTeam = m.home_team ?? (eff?.home ? findTeamInBracket(bracket, eff.home) : null)
                  const awayTeam = m.away_team ?? (eff?.away ? findTeamInBracket(bracket, eff.away) : null)

                  // Ghost if no teams assigned at all yet (match not yet reachable)
                  const isGhosted = !eff?.home && !eff?.away
                  // Non-active rounds OR individually locked matches are read-only
                  const isReadOnly = !isEditable || isGhosted || isMatchIndividuallyLocked(m)

                  const score = localScores.get(m.id)
                  return (
                    <MatchCard
                      key={m.id}
                      match={{ ...m, home_team_id: eff?.home ?? m.home_team_id, away_team_id: eff?.away ?? m.away_team_id, home_team: homeTeam, away_team: awayTeam }}
                      prediction={predMap.get(m.id)}
                      localWinnerId={localWinners.get(m.id)}
                      localHomeScore={score?.home}
                      localAwayScore={score?.away}
                      isLocked={isReadOnly}
                      isGhosted={isGhosted}
                      onPickTeam={handlePickTeam}
                      onScoreChange={handleScoreChange}
                      showScoreInputs={showScores && isEditable && !isMatchIndividuallyLocked(m)}
                      nowSecs={nowSecs}
                      lockBufferSecs={MATCH_LOCK_BUFFER_SECS}
                    />
                  )
                })}
              </div>
            </div>
          )
        })}
    </div>
  )

  return (
    <div className="flex flex-col gap-6">
      {/* Controls — show whenever there are any pickable matches */}
      {!isLocked && totalPickable > 0 && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <div
                className={`w-10 h-5 rounded-full transition-colors ${showScores ? 'bg-emerald-500' : 'bg-white/20'}`}
                onClick={() => setShowScores(v => !v)}
              >
                <div className={`w-4 h-4 rounded-full bg-white mt-0.5 transition-transform ${showScores ? 'translate-x-5 ml-1' : 'translate-x-1'}`} />
              </div>
              <span className="text-sm text-white/70">{t('bracket.scorePredictions')}</span>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-white/50">
              {t('bracket.picked', { count: pickedCount, total: totalPickable })}
            </span>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || pickedCount === 0}
              className="btn-primary btn-sm"
            >
              {isSubmitting ? <Spinner size="sm" /> : null}
              {t('bracket.save')}
            </button>
          </div>
        </div>
      )}

      {/* Main bracket: R32 → R16 → QF → SF → FINAL */}
      <div className="overflow-x-auto pb-4">
        <div className="inline-flex gap-8 min-w-max">
          {renderRound(bracket.rounds, ['R32', 'R16', 'QF', 'SF', 'FINAL'])}
        </div>
      </div>

      {/* 3rd place matches */}
      {bracket.rounds.some(r => r.round === '3RD') && (
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-white/50 mb-3">{t('bracket.thirdPlace')}</div>
          <div className="overflow-x-auto pb-4">
            <div className="inline-flex gap-4">
              {renderRound(bracket.rounds, ['3RD'])}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function findTeamInBracket(bracket: Bracket, teamId: string) {
  for (const round of bracket.rounds) {
    for (const match of round.matches) {
      if (match.home_team?.id === teamId) return match.home_team
      if (match.away_team?.id === teamId) return match.away_team
    }
  }
  return null
}
